
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, FileText, Calendar, Settings, MessageSquare, Folder, Clipboard, File, Receipt, Shield, Stethoscope, Heart } from "lucide-react";
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from "@/components/ui/popover";
import ROUTES from '@/routes-map';

const navItems = [
  { 
    icon: Home, 
    label: "Home", 
    path: ROUTES.PATIENT.HOME
  },
  { 
    icon: FileText, 
    label: "My Health", 
    path: ROUTES.PATIENT.MY_HEALTH.ROOT,
    isPopover: true,
    subItems: [
      { label: "Submitted Cases", path: ROUTES.PATIENT.MY_HEALTH.SUBMITTED_CASES, icon: Folder },
      { label: "Treatment Plans", path: ROUTES.PATIENT.MY_HEALTH.TREATMENT_PLANS, icon: Clipboard },
      { label: "Appointments", path: ROUTES.PATIENT.MY_HEALTH.APPOINTMENTS, icon: Calendar },
      { label: "Medical Documents", path: ROUTES.PATIENT.MY_HEALTH.MEDICAL_DOCUMENTS, icon: File },
      { label: "Purchased Services", path: ROUTES.PATIENT.MY_HEALTH.PURCHASED_SERVICES, icon: Receipt },
      { label: "Insurance", path: ROUTES.PATIENT.MY_HEALTH.INSURANCE, icon: Shield },
      { label: "Follow-up Care", path: ROUTES.PATIENT.MY_HEALTH.FOLLOW_UP_CARE, icon: Stethoscope },
      { label: "My Favorites", path: ROUTES.PATIENT.MY_HEALTH.FAVORITES, icon: Heart },
    ]
  },
  { 
    icon: Calendar, 
    label: "Itinerary", 
    path: ROUTES.PATIENT.ITINERARY
  },
  { 
    icon: MessageSquare, 
    label: "Messages", 
    path: ROUTES.PATIENT.MESSAGES.ROOT
  },
  { 
    icon: Settings, 
    label: "Settings", 
    path: "/patient-area/profile/settings",
    isPopover: true,
    subItems: [
      { label: "Personal Information", path: "/patient-area/profile/personal-information", icon: Home },
      { label: "Medical Data", path: "/patient-area/profile/medical-data", icon: FileText },
      { label: "Settings", path: "/patient-area/profile/settings", icon: Settings },
      { label: "Privacy & Legal", path: "/patient-area/profile/legal", icon: Shield },
      { label: "Support", path: "/patient-area/profile/support", icon: MessageSquare },
    ]
  }
];

const BottomNav = () => {
  const location = useLocation();
  
  // Helper function to check if a path is active
  const isPathActive = (path: string) => {
    if (path === ROUTES.PATIENT.HOME && location.pathname === ROUTES.PATIENT.HOME) return true;
    
    const currentPath = location.pathname;
    const basePath = typeof path === 'string' ? path.split('/').pop() : ''; // Get the last segment
    const baseCurrentPath = currentPath.split('/').pop();
    
    return currentPath === path || 
           (path !== ROUTES.PATIENT.HOME && currentPath.includes(basePath || '')) || 
           (baseCurrentPath === basePath && baseCurrentPath !== '');
  };
  
  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-around py-2">
          {navItems.map((item, index) => {
            const itemPath = typeof item.path === 'string' ? item.path : '';
            const isActive = isPathActive(itemPath);
            
            if (item.isPopover) {
              return (
                <Popover key={index}>
                  <PopoverTrigger asChild>
                    <button
                      className={`flex flex-col items-center justify-center py-1 px-3 ${
                        isActive ? 'text-primary' : 'text-muted-foreground'
                      }`}
                    >
                      <item.icon className="h-5 w-5 mb-1" />
                      <span className="text-xs">{item.label}</span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent 
                    className="w-60 p-0 bg-background shadow-lg rounded-md"
                    align="center"
                    side="top"
                  >
                    <div className="p-1">
                      {item.subItems?.map((subItem, subIndex) => (
                        <Link
                          key={subIndex}
                          to={subItem.path}
                          className="flex items-center w-full text-left px-4 py-2 text-sm hover:bg-accent rounded-sm"
                        >
                          {subItem.icon && <subItem.icon className="h-4 w-4 mr-2" />}
                          {subItem.label}
                        </Link>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              );
            }
            
            return (
              <Link 
                key={index} 
                to={itemPath}
                className={`flex flex-col items-center justify-center py-1 px-3 ${
                  isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              >
                <item.icon className="h-5 w-5 mb-1" />
                <span className="text-xs">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

export default BottomNav;
